import { z } from 'zod'

/** Public identifier: a random UUID. Never a sequential integer. */
export const publicIdSchema = z.uuid()

/** UTC ISO-8601 timestamp, e.g. `2026-08-14T02:30:00.000Z`. */
export const utcTimestampSchema = z.iso.datetime()

/**
 * Normalisation happens before validation, so a pasted `" User@Example.COM "`
 * is accepted and stored in the single canonical form the unique index and
 * every lookup rely on.
 */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254))

/** URL-safe competition slug, unique platform-wide. */
export const slugSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Must be lowercase alphanumeric words separated by hyphens.')

export const displayNameSchema = z.string().trim().min(1).max(120)

/** Every IANA zone name the runtime's `Intl` implementation recognizes. */
const SUPPORTED_TIME_ZONES = new Set<string>(Intl.supportedValuesOf('timeZone'))

export const ianaTimeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((value) => SUPPORTED_TIME_ZONES.has(value), 'Must be a supported IANA timezone.')

/**
 * Official-website-style field: HTTPS-only, nullable, empty/whitespace-only
 * input normalizes to `null` rather than being rejected — clearing the field
 * is a valid save, not a validation error. `http://` and other schemes
 * (including `javascript:`) are rejected outright rather than silently
 * upgraded, since a scheme mismatch usually means the value was pasted
 * wrong.
 */
export const nullableHttpsUrlSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') {
      return value
    }
    const trimmed = value.trim()
    return trimmed.length === 0 ? null : trimmed
  },
  z
    .url({ protocol: /^https$/ })
    .max(2048)
    .nullable(),
)

export const localeSchema = z
  .string()
  .min(2)
  .max(16)
  .regex(/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/, 'Must be a BCP 47 language tag.')

export const nullableUrlSchema = z.url().max(2048).nullable()

/**
 * DM-030/DATA-036, GOV-028 (Wave T). A free-text, self-declared "unit /
 * organization (self-reported)" label — deliberately just a length-bounded
 * string like `displayNameSchema`, never a lookup against any verified
 * organization record. Output encoding against HTML/script injection
 * (SEC-028) is the renderer's job (React escapes by default); this schema
 * only bounds length and trims whitespace.
 */
export const organizationDisplayNameSchema = z.string().trim().max(160)
